import { pgTable, integer, varchar, timestamp, text, boolean, jsonb, uuid, serial, check, index, uniqueIndex, foreignKey, date } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { DEFAULT_DEMO_BOOKING_CONFIG, DEFAULT_DYNAMIC_PRICING_CONFIG } from '../types/domains/club/constraints.js';
import { colorVariantColumn } from './columns.js';
export const tenants = pgTable('tenants', {
    id: serial('id').primaryKey(),
    name: varchar('name', { length: 255 }).notNull(),
    handle: varchar('handle', { length: 100 }).notNull().unique(),
    publicHandle: varchar('public_handle', { length: 100 }).unique(),
    appBundleId: varchar('app_bundle_id', { length: 100 }),
    iosAppId: varchar('ios_app_id', { length: 50 }),
    androidPackageName: varchar('android_package_name', { length: 100 }),
    iosTeamId: varchar('ios_team_id', { length: 10 }),
    androidCertFingerprints: text('android_cert_fingerprints').array(),
    colorTheme: varchar('color_theme', { length: 20 }).notNull().default('purple').$type(),
    isWhiteLabel: boolean('is_white_label').notNull().default(false),
    appStatus: varchar('app_status', { length: 20 }).notNull().default('none').$type(),
    iosSubmittedOn: date('ios_submitted_on'),
    iosLiveOn: date('ios_live_on'),
    androidSubmittedOn: date('android_submitted_on'),
    androidLiveOn: date('android_live_on'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow().$onUpdate(() => new Date()),
}, (table) => ({
    tenantAppStatusCheck: check('tenant_app_status_check', sql `${table.appStatus} IN ('none', 'in_progress', 'live')`),
    handleIdx: index('tenants_handle_idx').on(table.handle),
    publicHandleIdx: index('tenants_public_handle_idx').on(table.publicHandle),
}));
export const clubs = pgTable('clubs', {
    id: serial('id').primaryKey(),
    tenantId: integer('tenant_id')
        .notNull()
        .references(() => tenants.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    name: varchar('name', { length: 255 })
        .notNull(),
    status: varchar('status', { length: 20 })
        .notNull()
        .default('active'),
    timezone: varchar('timezone', { length: 100 })
        .notNull()
        .$type(),
    email: varchar('email', { length: 255 })
        .notNull()
        .default(''),
    phone: varchar('phone', { length: 50 })
        .notNull()
        .default(''),
    address: varchar('address', { length: 255 })
        .notNull(),
    city: varchar('city', { length: 255 }),
    state: varchar('state', { length: 255 }),
    zipCode: varchar('zip_code', { length: 255 }),
    country: varchar('country', { length: 2 })
        .notNull()
        .default('US')
        .$type(),
    geofence: jsonb('geofence')
        .$type(),
    logo: jsonb('logo')
        .$type(),
    gallery: jsonb('gallery')
        .$type(),
    primeTimeEnabled: boolean('prime_time_enabled')
        .notNull()
        .default(false),
    membershipAccessEnabled: boolean('membership_access_enabled')
        .notNull()
        .default(false),
    subscriptionOnlyBookings: boolean('subscription_only_bookings')
        .notNull()
        .default(false),
    guestFeesEnabled: boolean('guest_fees_enabled')
        .notNull()
        .default(false),
    demoBookingsEnabled: boolean('demo_bookings_enabled')
        .notNull()
        .default(false),
    simulatorCompanionEnabled: boolean('simulator_companion_enabled')
        .notNull()
        .default(false),
    simulatorCompanionConfig: jsonb('simulator_companion_config')
        .notNull()
        .$type()
        .default({}),
    passProcessingFees: boolean('pass_processing_fees')
        .notNull()
        .default(false),
    deferBookingPayments: boolean('defer_booking_payments')
        .notNull()
        .default(false),
    deferBookingsConfig: jsonb('defer_bookings_config')
        .$type(),
    pricingMode: varchar('pricing_mode', { length: 20 })
        .notNull()
        .default('scalingPrice'),
    hourlyRateCents: integer('hourly_rate_cents')
        .notNull()
        .default(5000),
    timeBasedEnabled: boolean('time_based_enabled')
        .notNull()
        .default(false),
    usageLimitMode: varchar('usage_limit_mode', { length: 20 })
        .notNull()
        .default('hours')
        .$type(),
    slotIntervalMinutes: integer('slot_interval_minutes')
        .notNull()
        .default(30),
    maxSessionMinutes: integer('max_session_minutes')
        .notNull()
        .default(120),
    minSessionMinutes: integer('min_session_minutes'),
    advanceBookingDays: integer('advance_booking_days')
        .notNull()
        .default(14),
    minNoticeMinutes: integer('min_notice_minutes')
        .notNull()
        .default(0),
    bookingGraceMinutes: integer('booking_grace_minutes')
        .notNull()
        .default(3),
    publicHours: jsonb('public_hours')
        .$type(),
    membershipHours: jsonb('membership_hours')
        .$type(),
    scheduleExceptions: jsonb('schedule_exceptions')
        .$type(),
    demoBookingConfig: jsonb('demo_booking_config')
        .notNull()
        .$type()
        .default(DEFAULT_DEMO_BOOKING_CONFIG),
    defaultPricing: jsonb('default_pricing')
        .$type()
        .notNull()
        .default({ '30': null, '60': null, '90': null, '120': null, '150': null, '180': null }),
    primeTimeConfig: jsonb('prime_time_config')
        .$type(),
    timeBasedPricingConfig: jsonb('time_based_pricing_config')
        .$type(),
    dynamicPricingConfig: jsonb('dynamic_pricing_config')
        .notNull()
        .$type()
        .default(DEFAULT_DYNAMIC_PRICING_CONFIG),
    guestFeeConfig: jsonb('guest_fee_config')
        .notNull()
        .$type()
        .default({ freeGuestsPerBooking: 2, feePerGuest: 1500 }),
    settings: jsonb('settings')
        .$type()
        .notNull()
        .default({}),
    availabilityConfig: jsonb('availability_config')
        .$type(),
    createdAt: timestamp('created_at', { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, (table) => ({
    clubStatusCheck: check('club_status_check', sql `${table.status} IN ('active', 'inactive')`),
    slotIntervalCheck: check('slot_interval_check', sql `${table.slotIntervalMinutes} IN (10, 15, 30, 60)`),
    maxSessionCheck: check('max_session_check', sql `${table.maxSessionMinutes} IN (60, 120, 180, 240)`),
    minSessionCheck: check('min_session_check', sql `${table.minSessionMinutes} IS NULL OR ${table.minSessionMinutes} IN (30, 60, 90, 120)`),
    advanceBookingDaysCheck: check('advance_booking_days_check', sql `${table.advanceBookingDays} > 0`),
    minNoticeCheck: check('min_notice_check', sql `${table.minNoticeMinutes} >= 0 AND ${table.minNoticeMinutes} <= 43200`),
    bookingGraceCheck: check('booking_grace_check', sql `${table.bookingGraceMinutes} >= 0 AND ${table.bookingGraceMinutes} <= 60`),
    pricingModeCheck: check('pricing_mode_check', sql `${table.pricingMode} IN ('setPrice', 'scalingPrice', 'dynamicPricing')`),
    usageLimitModeCheck: check('usage_limit_mode_check', sql `${table.usageLimitMode} IN ('hours', 'sessions')`),
    scheduleExceptionKindCheck: check('schedule_exception_kind_check', sql `${table.scheduleExceptions} IS NULL OR NOT jsonb_path_exists(${table.scheduleExceptions}, '$.* ? (!(@.kind == "hours" || @.kind == "closed") || @.schedule.size() == 0)')`),
    idTenantUnique: uniqueIndex('clubs_id_tenant_unique').on(table.id, table.tenantId),
}));
export const IDENTITY_PROVIDERS = ['supabase'];
export const identities = pgTable('identities', {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: integer('tenant_id').notNull().references(() => tenants.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    primaryEmail: text('primary_email').notNull(),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow().$onUpdate(() => new Date()),
}, (table) => ({
    tenantEmailUnique: uniqueIndex('identities_tenant_email_unique')
        .on(table.tenantId, table.primaryEmail)
        .where(sql `${table.deletedAt} IS NULL`),
    idTenantUnique: uniqueIndex('identities_id_tenant_unique').on(table.id, table.tenantId),
    tenantIdx: index('identities_tenant_idx').on(table.tenantId),
    primaryEmailIdx: index('identities_primary_email_idx')
        .on(table.primaryEmail)
        .where(sql `${table.deletedAt} IS NULL`),
}));
export const identityProviders = pgTable('identity_providers', {
    id: uuid('id').primaryKey().defaultRandom(),
    identityId: uuid('identity_id').notNull().references(() => identities.id, { onDelete: 'cascade' }),
    provider: text('provider').notNull().$type(),
    providerUserId: text('provider_user_id').notNull(),
    emailAtLink: text('email_at_link'),
    linkedAt: timestamp('linked_at', { withTimezone: true }).notNull().defaultNow(),
}, (table) => ({
    providerIdentityUnique: uniqueIndex('identity_providers_provider_identity_unique')
        .on(table.provider, table.identityId),
    identityIdx: index('identity_providers_identity_idx').on(table.identityId),
    providerUserIdx: index('identity_providers_provider_user_idx').on(table.provider, table.providerUserId),
}));
export const users = pgTable('users', {
    id: serial('id').primaryKey(),
    tenantId: integer('tenant_id').notNull().references(() => tenants.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    identityId: uuid('identity_id').notNull(),
    supabaseUserId: uuid('supabase_user_id'),
    firstName: varchar('first_name', { length: 255 }).notNull().default('[unknown]'),
    lastName: varchar('last_name', { length: 255 }).notNull().default('[unknown]'),
    email: varchar('email', { length: 255 }).notNull(),
    phone: varchar('phone', { length: 50 }).notNull(),
    authStatus: varchar('status', { length: 50 }).notNull().default('pending').$type(),
    accountOrigin: varchar('account_origin', { length: 20 }).$type(),
    marketingState: varchar('marketing_state', { length: 50 }).$type(),
    gender: varchar('gender', { length: 50 }),
    age: integer('age'),
    company: varchar('company', { length: 255 }),
    accountType: varchar('account_type', { length: 50 }).notNull().default('member').$type(),
    billingAddress: text('billing_address'),
    billingEmail: varchar('billing_email', { length: 255 }),
    notes: text('notes'),
    credit: integer('credit').notNull().default(0),
    profileImage: jsonb('profile_image').$type(),
    preferences: jsonb('preferences').$type().notNull().default({}),
    color: colorVariantColumn(),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow().$onUpdate(() => new Date()),
}, (table) => ({
    identityTenantFk: foreignKey({
        name: 'users_identity_tenant_fk',
        columns: [table.identityId, table.tenantId],
        foreignColumns: [identities.id, identities.tenantId],
    }).onDelete('restrict'),
    identityTenantUnique: uniqueIndex('users_identity_tenant_unique')
        .on(table.identityId, table.tenantId)
        .where(sql `${table.deletedAt} IS NULL`),
    userStatusCheck: check('user_status_check', sql `${table.authStatus} IN ('active', 'inactive', 'pending', 'incomplete', 'suspended', 'onboarding', 'complete')`),
    accountOriginCheck: check('users_account_origin_check', sql `${table.accountOrigin} IN ('signup', 'invite', 'import', 'payment', 'gift', 'staff', 'unknown')`),
    marketingStateCheck: check('marketing_state_check', sql `${table.marketingState} IN ('subscribed', 'lead', 'churned', 'lapsed', 'trial', 'delinquent')`),
    accountTypeCheck: check('account_type_check', sql `${table.accountType} IN ('member', 'corporate', 'guest')`),
    userCreditCheck: check('user_credit_check', sql `${table.credit} >= 0`),
    emailTenantUnique: uniqueIndex('users_email_tenant_ci_unique')
        .on(sql `lower(${table.email})`, table.tenantId)
        .where(sql `${table.deletedAt} IS NULL`),
    statusIdx: index('users_status_idx').on(table.authStatus),
    supabaseUserIdIdx: index('users_supabase_user_id_idx').on(table.supabaseUserId),
    idTenantUnique: uniqueIndex('users_id_tenant_unique').on(table.id, table.tenantId),
}));
export const clubAdmins = pgTable('club_admins', {
    id: serial('id').primaryKey(),
    tenantId: integer('tenant_id').notNull().references(() => tenants.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    identityId: uuid('identity_id').notNull(),
    supabaseUserId: uuid('supabase_user_id'),
    firstName: varchar('first_name', { length: 255 }).notNull(),
    lastName: varchar('last_name', { length: 255 }).notNull(),
    kind: varchar('kind', { length: 50 }).notNull().default('admin').$type(),
    status: varchar('status', { length: 50 }).notNull().default('active').$type(),
    lastLogin: timestamp('last_login', { withTimezone: true }),
    email: varchar('email', { length: 255 }).notNull(),
    permissions: text('permissions').array().notNull().default([]),
    profileImage: jsonb('profile_image').$type(),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow().$onUpdate(() => new Date()),
}, (table) => ({
    identityTenantFk: foreignKey({
        name: 'club_admins_identity_tenant_fk',
        columns: [table.identityId, table.tenantId],
        foreignColumns: [identities.id, identities.tenantId],
    }).onDelete('restrict'),
    identityTenantUnique: uniqueIndex('club_admins_identity_tenant_unique')
        .on(table.identityId, table.tenantId)
        .where(sql `${table.deletedAt} IS NULL`),
    clubAdminKindCheck: check('club_admin_kind_check', sql `${table.kind} IN ('admin', 'instructor')`),
    clubAdminStatusCheck: check('club_admin_status_check', sql `${table.status} IN ('active', 'inactive', 'suspended')`),
    emailCiIdx: index('club_admins_email_ci_idx')
        .on(sql `lower(${table.email})`)
        .where(sql `${table.deletedAt} IS NULL`),
    supabaseUserIdIdx: index('club_admins_supabase_user_id_idx').on(table.supabaseUserId),
    idTenantUnique: uniqueIndex('club_admins_id_tenant_unique').on(table.id, table.tenantId),
}));
export const invitationTokens = pgTable('invitation_tokens', {
    id: serial('id').primaryKey(),
    token: varchar('token', { length: 64 }).notNull().unique(),
    userId: integer('user_id').references(() => users.id, { onDelete: 'cascade' }),
    email: varchar('email', { length: 255 }).notNull(),
    purpose: varchar('purpose', { length: 20 }).notNull(),
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    usedAt: timestamp('used_at', { withTimezone: true }),
    lastSentAt: timestamp('last_sent_at', { withTimezone: true }),
    applicationType: varchar('application_type', { length: 50 }),
    metadata: jsonb('metadata'),
}, (table) => ({
    purposeCheck: check('purpose_check', sql `${table.purpose} IN ('invitation', 'password-reset')`),
    tokenIdx: index('invitation_tokens_token_idx').on(table.token),
    userIdIdx: index('invitation_tokens_user_id_idx').on(table.userId),
    emailIdx: index('invitation_tokens_email_idx').on(table.email),
    expiresAtIdx: index('invitation_tokens_expires_at_idx').on(table.expiresAt),
    purposeUserIdx: index('invitation_tokens_purpose_user_idx').on(table.purpose, table.userId),
}));
//# sourceMappingURL=core.js.map